import { CalendarDays, ChevronLeft, ListMusic, MessagesSquare, Settings, Shield, Users, WifiOff, type LucideIcon } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, type ReactNode, type RefObject } from 'react';
import { NavLink, Outlet, useLocation, useNavigate, useNavigationType } from 'react-router';
import { useAppBadge, useNotifications } from '../api/notifications';
import { useMe } from '../auth/AuthProvider';
import { NotificationBell } from '../notify/NotificationBell';
import { badgeText } from '../notify/format';
import { DownloadsBar } from '../offline/DownloadsBar';
import { useOnline } from '../lib/useOnline';
import { useT, type MessageKey } from '../i18n';
import { ClefMark, Wordmark } from './brand';
import { restoreStep, scrollActionFor, shellScroll } from './scrollMemory';
import { cx } from './ui';
import { roleAtLeast } from '../../shared/audience';

interface NavItem {
  to: string;
  label: MessageKey;
  icon: LucideIcon;
  end?: boolean;
  /** Unread count shown on the icon. */
  badge?: number;
}

function useNavItems(chatUnread: number): NavItem[] {
  const me = useMe();
  const items: NavItem[] = [
    { to: '/', label: 'nav.home', icon: CalendarDays, end: true },
    { to: '/songs', label: 'nav.songs', icon: ListMusic },
    { to: '/chat', label: 'nav.chat', icon: MessagesSquare, badge: chatUnread },
    { to: '/choir', label: 'nav.choir', icon: Users },
    { to: '/settings', label: 'nav.settings', icon: Settings },
  ];
  if (me && roleAtLeast(me.role, 'editor')) items.push({ to: '/admin', label: 'nav.admin', icon: Shield });
  return items;
}

export function OfflineBanner() {
  const online = useOnline();
  const t = useT();
  if (online) return null;
  return (
    <div className="flex items-center justify-center gap-2 bg-amber-100 px-4 py-1.5 text-[15px] font-medium text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">
      <WifiOff className="size-4" aria-hidden />
      {t('common.offline_hint')}
    </div>
  );
}

/** Any of these on <main> means the user is scrolling: a pending restore stops. */
const USER_SCROLL_EVENTS = ['wheel', 'touchstart', 'pointerdown', 'keydown'] as const;

/**
 * Scroll position of the shell's <main> per history entry (see scrollMemory.ts): a new screen starts
 * at the top, Back/Forward returns to where the user was — also after a song page, which unmounts
 * the shell — and same-screen updates (?q= / ?tab= written with replace, #hash links) keep it.
 * Hash targets (EventScreen #spjall, Settings #settings-offline) scroll in their own effects, which
 * run after this layout effect, so they still win.
 */
function useShellScroll(mainRef: RefObject<HTMLElement | null>) {
  const location = useLocation();
  const navType = useNavigationType();
  const keyRef = useRef(location.key);
  const prevPathname = useRef<string | null>(null);

  // Record the current entry's offset. Scroll events already arrive at most once per frame.
  useEffect(() => {
    const main = mainRef.current;
    if (!main) return;
    const onScroll = () => shellScroll.set(keyRef.current, main.scrollTop);
    main.addEventListener('scroll', onScroll, { passive: true });
    return () => main.removeEventListener('scroll', onScroll);
  }, [mainRef]);

  useLayoutEffect(() => {
    const main = mainRef.current;
    if (!main) return;
    keyRef.current = location.key;
    const action = scrollActionFor({
      type: navType,
      pathname: location.pathname,
      prevPathname: prevPathname.current,
      saved: shellScroll.get(location.key),
    });
    prevPathname.current = location.pathname;
    if (action.kind !== 'restore') {
      if (action.kind === 'top') main.scrollTop = 0;
      // A replace makes a new entry: it starts where the old one was.
      shellScroll.set(location.key, main.scrollTop);
      return;
    }
    // The list may render a frame or two later (cache restore): retry until it is tall enough.
    const target = action.top;
    const started = performance.now();
    let raf = 0;
    const stop = () => {
      cancelAnimationFrame(raf);
      for (const ev of USER_SCROLL_EVENTS) main.removeEventListener(ev, stop);
    };
    const step = () => {
      const { top, again } = restoreStep(target, main.scrollHeight - main.clientHeight, performance.now() - started);
      main.scrollTop = top;
      if (again) raf = requestAnimationFrame(step);
      else stop();
    };
    for (const ev of USER_SCROLL_EVENTS) main.addEventListener(ev, stop, { passive: true });
    step();
    return stop;
  }, [mainRef, location.key, location.pathname, navType]);
}

/** Unread count on a nav icon (the label keeps the name; the count is read out with it). */
function NavBadge({ n }: { n: number | undefined }) {
  if (!n) return null;
  return (
    <span aria-hidden className="absolute -top-1.5 left-[calc(50%+6px)] h-5 min-w-5 rounded-full bg-[var(--danger)] px-1 text-center text-[12px] leading-5 font-bold text-white">
      {badgeText(n)}
    </span>
  );
}

function NavBadgeLabel({ n }: { n: number | undefined }) {
  const t = useT();
  return n ? <span className="sr-only">, {t('chat.unread', { count: n })}</span> : null;
}

/**
 * App chrome: left rail on tablets/desktop, bottom tab bar on phones. Keeps the notification bell's poll
 * running on every screen of the shell (once a minute while visible) and the app icon's badge in step.
 */
export function AppShell() {
  const bell = useNotifications(useMe());
  useAppBadge(bell.data?.total);
  const chatUnread = (bell.data?.items ?? []).reduce((n, it) => (it.kind === 'chat' ? n + it.unread : n), 0);
  const items = useNavItems(chatUnread);
  const t = useT();
  const mainRef = useRef<HTMLElement>(null);
  useShellScroll(mainRef);
  return (
    <div className="flex h-dvh w-full overflow-hidden" style={{ paddingLeft: 'var(--safe-left)', paddingRight: 'var(--safe-right)' }}>
      <nav
        className="hidden w-[92px] shrink-0 flex-col items-center gap-1 border-r border-[var(--line)] bg-[var(--surface)] md:flex"
        style={{ paddingTop: 'calc(var(--top-inset) + 12px)', paddingBottom: 'calc(var(--safe-bottom) + 12px)' }}
        aria-label={t('app.name')}
      >
        <div className="mb-4 px-2 text-center text-[var(--accent)]">
          <ClefMark size={52} />
        </div>
        {items.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.end}
            className={({ isActive }) =>
              cx(
                'flex w-[76px] flex-col items-center gap-1 rounded-2xl py-2.5 text-[13px] font-semibold transition',
                isActive ? 'bg-[var(--accent-soft)] text-[var(--accent)]' : 'text-[var(--muted)] hover:text-[var(--ink)]',
              )
            }
          >
            <span className="relative">
              <it.icon className="size-7" aria-hidden />
              <NavBadge n={it.badge} />
            </span>
            {t(it.label)}
            <NavBadgeLabel n={it.badge} />
          </NavLink>
        ))}
      </nav>
      <div className="flex min-w-0 flex-1 flex-col">
        <OfflineBanner />
        <DownloadsBar />
        <main ref={mainRef} className="scroll-y relative min-h-0 flex-1" id="main">
          <Outlet />
        </main>
        <nav
          className="flex shrink-0 border-t border-[var(--line)] bg-[var(--surface)] md:hidden"
          style={{ paddingBottom: 'var(--safe-bottom)' }}
          aria-label={t('app.name')}
        >
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              className={({ isActive }) =>
                cx(
                  // Six tabs (editors) on a 360 pt phone: each may shrink to its share and cut its label.
                  'flex min-h-14 min-w-0 flex-1 flex-col items-center justify-center gap-0.5 px-0.5 font-semibold',
                  items.length > 5 ? 'text-[11px]' : 'text-[12px]',
                  isActive ? 'text-[var(--accent)]' : 'text-[var(--muted)]',
                )
              }
            >
              <span className="relative">
                <it.icon className="size-6" aria-hidden />
                <NavBadge n={it.badge} />
              </span>
              <span className="max-w-full truncate">{t(it.label)}</span>
              <NavBadgeLabel n={it.badge} />
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}

/**
 * Standard page: sticky header (title, optional back, actions, the notification bell) + centred content.
 * `fill`: the content stretches to the bottom of the screen without bottom padding (the chat, whose
 * composer sticks to the bottom).
 */
export function Page({
  title,
  back,
  actions,
  children,
  wide,
  brand,
  fill,
}: {
  title?: ReactNode;
  back?: string | true;
  actions?: ReactNode;
  children: ReactNode;
  wide?: boolean;
  brand?: boolean;
  fill?: boolean;
}) {
  const navigate = useNavigate();
  const t = useT();
  const me = useMe();
  return (
    <div className={cx('min-h-full', fill && 'flex flex-col')}>
      <header
        className="sticky top-0 z-20 border-b border-[var(--line)] bg-[var(--bg)]"
        style={{ paddingTop: 'var(--top-inset)' }}
      >
        <div className={cx('mx-auto flex min-h-16 items-center gap-2 px-4 md:px-8', wide ? 'max-w-6xl' : 'max-w-4xl')}>
          {back ? (
            <button
              type="button"
              onClick={() => (back === true ? navigate(-1) : navigate(back))}
              className="-ml-2 inline-flex min-h-11 items-center gap-1 rounded-xl pr-3 pl-1 font-semibold text-[var(--accent)] hover:bg-[var(--accent-soft)]"
            >
              <ChevronLeft className="size-6" aria-hidden />
              {t('common.back')}
            </button>
          ) : null}
          {brand ? <Wordmark compact className="md:hidden" /> : null}
          {title ? <h1 className={cx('min-w-0 flex-1 truncate text-2xl font-bold tracking-tight', brand && 'hidden md:block')}>{title}</h1> : <div className="flex-1" />}
          {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
          {me ? <NotificationBell /> : null}
        </div>
      </header>
      <div
        className={cx('mx-auto px-4 md:px-8', fill ? 'flex w-full flex-1 flex-col pt-3' : 'py-5 md:py-8', wide ? 'max-w-6xl' : 'max-w-4xl')}
        style={fill ? undefined : { paddingBottom: 'calc(var(--safe-bottom) + 24px)' }}
      >
        {children}
      </div>
    </div>
  );
}
